import React from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { Iphone } from './magicui/iphone';

interface AppPromotionProps {
  compact?: boolean;
  lang?: 'en' | 'fr' | 'de' | 'it';
}

const AppPromotion: React.FC<AppPromotionProps> = ({ compact = false, lang: propLang }) => {
  const { lang: paramLang } = useParams<{ lang?: string }>();
  const currentLang = (propLang || paramLang || 'en') as 'en' | 'fr' | 'de' | 'it';
  
  const translations = {
    en: {
      getFullExperience: 'Get the full experience!',
      experienceMore: 'Experience more with the app',
      features: 'Track multi-match ELO • Win predictions • Tournament finder • Dark theme',
      joinPlayers: 'Join 500+ Swiss players on mobile'
    },
    fr: {
      getFullExperience: 'Profite de l\'expérience complète!',
      experienceMore: 'Découvre plus avec l\'appli',
      features: 'Suivi ELO multi-matchs • Prédictions de victoire • Recherche de tournois • Mode sombre',
      joinPlayers: 'Rejoins plus de 500 joueurs suisses sur mobile'
    },
    de: {
      getFullExperience: 'Hol dir das volle Erlebnis!',
      experienceMore: 'Erlebe mehr mit der App',
      features: 'Multi-Match ELO-Tracking • Gewinnprognosen • Turnierfinder • Dunkles Design',
      joinPlayers: 'Über 500 Schweizer Spieler nutzen die App'
    },
    it: {
      getFullExperience: 'Ottieni l\'esperienza completa!',
      experienceMore: 'Scopri di più con l\'app',
      features: 'Tracciamento ELO multi-partita • Previsioni di vittoria • Ricerca tornei • Tema scuro',
      joinPlayers: 'Unisciti a oltre 500 giocatori svizzeri su mobile'
    }
  };
  
  const t = translations[currentLang] || translations.en;
  if (compact) {
    // Compact version for player/calculator pages - desktop only
    return (
      <motion.div
        id="app-promotion-compact"
        className="fixed bottom-6 right-6 z-40 hidden md:block"
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}>
        {/* Main card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 max-w-xs border border-gray-100 dark:border-gray-700 transition-colors duration-300">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 transition-colors duration-300">{t.getFullExperience}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 transition-colors duration-300">{t.features}</p>
          <div className="flex gap-2">
            <a
              href="https://apps.apple.com/ch/app/ttstats-swiss-table-tennis/id6744279609"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <img 
                src="/app-store-badge.svg" 
                alt="App Store" 
                className="h-8 w-full object-contain"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.peaktwilight.ttstats"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <img 
                src="/google-play-badge.svg" 
                alt="Google Play" 
                className="h-8 w-full object-contain"
              />
            </a>
          </div>
        </div>
      </motion.div>
    );
  }

  // Full version - minimal footer style (same as HomePage) - desktop only
  return (
    <motion.div
      id="app-promotion-section"
      className="bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 py-16 mt-auto border-t border-gray-100 dark:border-gray-800 hidden md:block transition-colors duration-300"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.3 }}>
      
      <div className="max-w-5xl mx-auto px-4">
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}>
          <h3 className="text-3xl font-extralight text-gray-800 dark:text-gray-200 mb-4 transition-colors duration-300">
            {t.experienceMore}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm transition-colors duration-300">
            {t.features}
          </p>
        </motion.div>

        {/* Phone mockup with floating animation */}
        <div className="flex justify-center items-center mb-10">
          <Iphone src="/app-screenshot.png" />
        </div>
          
        {/* App Store Badges - sleeker */}
        <div className="text-center">
          <div className="flex justify-center gap-6 mb-6">
            <motion.a
              href="https://apps.apple.com/ch/app/ttstats-swiss-table-tennis/id6744279609"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <img 
                src="/app-store-badge.svg" 
                alt="Download on App Store" 
                className="h-11 opacity-90 hover:opacity-100 transition-opacity"
              />
            </motion.a>
            <motion.a
              href="https://play.google.com/store/apps/details?id=com.peaktwilight.ttstats"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <img 
                src="/google-play-badge.svg" 
                alt="Get it on Google Play" 
                className="h-11 opacity-90 hover:opacity-100 transition-opacity"
              />
            </motion.a>
          </div>
          
          <p className="text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">{t.joinPlayers}</p>
        </div>

        {/* Legal Links */}
        <motion.div
          className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-800 transition-colors duration-300"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}>
          <div className="flex items-center justify-center gap-4 text-xs">
            <a href="/privacy" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors duration-300">
              Privacy Policy
            </a>
            <span className="text-gray-300 dark:text-gray-600 transition-colors duration-300">•</span>
            <a href="/terms" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors duration-300">
              Terms of Use
            </a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AppPromotion;